import { useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Lightweight WebSocket hook for live ops updates.
 *
 * - Connects to /api/ws/live with the user's bearer token via query param.
 * - Auto-reconnects with exponential backoff (max 30s).
 * - Falls back gracefully: if WS is closed, callers continue polling.
 * - Calls onEvent(eventObj) for every server push.
 *
 * Event shape: { type: "event.ingested" | "alert.created" | "device.status_changed" | "risk.changed" | "connected" | "heartbeat", org_id, data }
 */
export function useLiveUpdates(onEvent) {
  const { token } = useAuth();
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const backoffRef = useRef(1000);
  const handlerRef = useRef(onEvent);
  const heartbeatRef = useRef(null);
  const [status, setStatus] = useState("idle"); // idle | connecting | connected | disconnected

  // Keep latest handler without re-opening socket
  useEffect(() => { handlerRef.current = onEvent; }, [onEvent]);

  const cleanup = useCallback(() => {
    if (reconnectRef.current) { clearTimeout(reconnectRef.current); reconnectRef.current = null; }
    if (heartbeatRef.current) { clearInterval(heartbeatRef.current); heartbeatRef.current = null; }
    if (wsRef.current) {
      try { wsRef.current.close(); } catch (e) { /* noop */ }
      wsRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!token) { cleanup(); setStatus("idle"); return; }

    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      const httpBase = process.env.REACT_APP_BACKEND_URL || "";
      const wsBase = httpBase.replace(/^http/, "ws");
      const url = `${wsBase}/api/ws/live?token=${encodeURIComponent(token)}`;
      setStatus("connecting");

      let ws;
      try {
        ws = new WebSocket(url);
      } catch (e) {
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        if (cancelled) { try { ws.close(); } catch (e) { /* noop */ } return; }
        setStatus("connected");
        backoffRef.current = 1000; // reset backoff on success
        // Client-side keepalive
        heartbeatRef.current = setInterval(() => {
          try { ws.send("ping"); } catch (e) { /* noop */ }
        }, 25_000);
      };

      ws.onmessage = (ev) => {
        if (!ev.data || ev.data === "pong") return;
        let msg;
        try { msg = JSON.parse(ev.data); } catch (e) { return; }
        if (!msg || !msg.type) return;
        if (msg.type === "heartbeat" || msg.type === "connected") return;
        try { handlerRef.current?.(msg); } catch (e) { /* noop */ }
      };

      ws.onclose = () => {
        if (heartbeatRef.current) { clearInterval(heartbeatRef.current); heartbeatRef.current = null; }
        if (cancelled) { setStatus("idle"); return; }
        setStatus("disconnected");
        scheduleReconnect();
      };

      ws.onerror = () => { /* close handler will retry */ };
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      const delay = Math.min(backoffRef.current, 30_000);
      backoffRef.current = Math.min(backoffRef.current * 2, 30_000);
      reconnectRef.current = setTimeout(connect, delay);
    };

    connect();
    return () => { cancelled = true; cleanup(); };
  }, [token, cleanup]);

  return { status };
}
